import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Text } from "../../../components/Text";
import { Entry } from "../../../model/entry/Entry";
import { Spacing } from "../../../theme/Spacing";
import { TestIDs } from "../../../tests/types/testIds";
import { DuplicateEntryFieldDescriptor } from "../utils/duplicateEntryFields";
import { EntryKindBadge } from "./EntryKindBadge";
import { EntryMetaStrip } from "./EntryMetaStrip";
import { MarkedForDeletionBadge } from "./MarkedForDeletionBadge";
import { getEntryLabel } from "../../../utils/entryUtil";

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(6rem, 30%) minmax(0, 1fr);
  gap: ${Spacing.m};
  padding-block: ${Spacing.s};
  border-block-end: 1px solid ${({ theme }) => theme.sub};
`;

type Props = {
  entry: Entry | null;
  descriptors: DuplicateEntryFieldDescriptor[];
  note?: string;
  onClose: VoidFunction;
};

export const EntryDuplicatePreviewDrawer: React.FC<Props> = ({
  entry,
  descriptors,
  note,
  onClose,
}) => {
  const { t } = useTranslation();

  const title = getEntryLabel(entry || undefined);

  const rows = entry
    ? descriptors
        .filter((descriptor) => descriptor.actionable)
        .map((descriptor) => ({
          key: descriptor.key,
          label: descriptor.label,
          value: descriptor.formatValue(entry),
        }))
    : [];

  return (
    <Drawer
      title={t("entries.duplicates.preview.title")}
      open={Boolean(entry)}
      onClose={onClose}
      zIndex={2003}
      destroyOnHidden
      // The shared Drawer shows the chat widget again when it closes, but the
      // resolve drawer underneath is still open
      afterOpenChange={() => {}}
    >
      {entry ? (
        <Flex
          flexDirection="column"
          gap="m"
          dataTestId={TestIDs.entries.duplicates.resolve.previewDrawer}
        >
          <Flex alignItems="center" gap="s" flexWrap="wrap">
            <Text fontSize="m" fontWeight="bold" wordBreak="break-word">
              <bdi>{title}</bdi>
            </Text>
            <EntryKindBadge kind={entry.kind} />
            {entry.markedForDeletion ? <MarkedForDeletionBadge /> : null}
          </Flex>

          {note ? <Info info={note} /> : null}

          <EntryMetaStrip entry={entry} descriptors={descriptors} />

          <div>
            {rows.map(({ key, label, value }) => (
              <FieldRow key={key}>
                <Text fontSize="s" color="darkerSub">
                  {label}
                </Text>
                <Text
                  fontSize="s"
                  wordBreak="break-word"
                  color={value ? undefined : "darkerSub"}
                >
                  {value ? <bdi>{value}</bdi> : "-"}
                </Text>
              </FieldRow>
            ))}
          </div>
        </Flex>
      ) : null}
    </Drawer>
  );
};
